"use client";
import { useMutation } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { ObjectSchema, object, string } from "yup";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";

import { Routes } from "@/constants/routes";
import { graphql } from "@/gql/gql";
import { ErrorCode } from "@/gql/graphql";
import AnalyticsService from "@/service/analytics/AnalyticsService";
import { AnalyticEventType } from "@/service/analytics/Events";
import GqlService from "@/service/GqlService";
import LocalStorageService, {
  LocalStorageKey,
} from "@/service/LocalStorageService";

const logInMutation = graphql(`
  mutation LogIn($data: LogInInput!) {
    logIn(data: $data) {
      accessToken
      refreshToken
      user {
        id
        name
        email
        role
        status
        createdAt
        updatedAt
      }
    }
  }
`);

type FormValues = {
  email: string;
  password: string;
};

const schema: ObjectSchema<FormValues> = object({
  email: string().email().required(),
  password: string().required(),
});

export default function LogIn() {
  const router = useRouter();
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const form = useForm<FormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
  });
  const [logIn, { loading }] = useMutation(logInMutation, {
    onCompleted: async (data) => {
      LocalStorageService.setTokens(data.logIn);
      const user = data.logIn.user;
      await AnalyticsService.trackLogInSuccess(user);
      datadogRum.setUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        status: user.status,
      });
      router.push(Routes.dashboard);
    },
    onError: (e) => {
      e.graphQLErrors;
      if (GqlService.isErrorCode(e, ErrorCode.InvalidCredentials)) {
        toast({
          variant: "destructive",
          title: "Usuário ou senha incorretos.",
        });
        AnalyticsService.track(AnalyticEventType.LogInFailure, {
          reason: "invalid_credentials",
        });
        return;
      }
      AnalyticsService.track(AnalyticEventType.LogInFailure, {
        reason: "unexpected_error",
      });
      toast({
        variant: "destructive",
        title: "Erro inesperado, tente novamente mais tarde.",
      });
    },
  });

  useEffect(() => {
    if (LocalStorageService.getToken("accessToken")) {
      router.push(Routes.dashboard);
    }
  }, [router]);

  const onSubmit = async (values: FormValues) => {
    logIn({
      variables: {
        data: {
          email: values.email,
          password: values.password,
        },
      },
    });
  };

  // TODO: remove this when the marketing landing page is ready
  useEffect(() => {
    if (searchParams.has("ref")) {
      LocalStorageService.setItem(
        LocalStorageKey.referral_tag,
        searchParams.get("ref") ?? "",
      );
    }
  }, [searchParams]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Entrar na sua conta
        </h1>
        <p className="text-sm text-muted-foreground">
          Digite seu e-mail e senha para acessar sua conta
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input placeholder="seu@email.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="••••••••" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={!form.formState.isValid || loading}
          >
            Entrar
          </Button>
        </form>
      </Form>
      <div className="flex flex-col space-y-2 text-center text-sm">
        <Link
          href={Routes.forgotPassword}
          className="text-muted-foreground hover:underline"
        >
          Esqueceu sua senha?
        </Link>
        <p>
          Ainda não tem uma conta?{" "}
          <Link
            href={Routes.register}
            className="text-juriblue hover:underline"
          >
            Cadastre-se
          </Link>
        </p>
      </div>
    </div>
  );
}
